import {
  OutlinedInput,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Box,
  Switch,
  TableCell,
  FormControlLabel,
  InputAdornment,
  Card,
  CardActions,
  Typography,
} from '@mui/material'
import { updateSystemSettings } from 'api/systemSettings'
import LoadingButton from 'components/ui/LoadingButton'
import { CONFIDENCE_COLOR } from 'config'
import { useAlert } from 'hooks/useDialog'
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useNavigate } from 'react-router-dom'

import { useConfidenceConfigLoaderData } from './ConfidenceConfig.loader'
const ERROR_MSG_REQUIRED = '必須入力項目です'
const ERROR_MSG_HIGH_MIN_RANGE = '1以上99以下の整数を入力してください'
const ERROR_MSG_MID_MIN_RANGE = '0以上98以下の整数を入力してください'
const ERROR_MSG_MID_MIN_LARGE = '高精度より小さい値を入力してください'
const ERROR_MSG_HIGH_MIN_SMALL = '中精度より大きい値を入力してください'

const ConfidenceLabelBox = (props) => (
  <Box
    sx={{
      p: 1,
      br: 4,
      border: '1px solid transparent',
    }}
    {...props}
  />
)

const ConfidenceActions = (props) => (
  <CardActions
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: 2,
    }}
    {...props}
  />
)

const MyNumericFormat = (props) => (
  <NumericFormat
    customInput={OutlinedInput}
    size="small"
    margin="dense"
    decimalScale={0}
    isAllowed={(values) => {
      const { floatValue } = values
      return floatValue === undefined || (floatValue > 0 && floatValue < 100)
    }}
    thousandSeparator
    data-testid={props.testid}
    {...props}
  />
)

function ConfidenceConfig() {
  const data = useConfidenceConfigLoaderData()
  const { highMin, midMin, confidence } = data

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    formState,
    getValues,
    trigger,
  } = useForm({
    mode: 'all',
  })
  const draft = watch()
  const showLowConfidenceColor = draft.confidence && CONFIDENCE_COLOR.low
  const showMidConfidenceColor = draft.confidence && CONFIDENCE_COLOR.mid

  const [saving, setSaving] = useState(false)
  const alert = useAlert()

  useEffect(() => {
    trigger()
  }, [trigger, draft.midMin, draft.highMin])

  const navigate = useNavigate()
  const handleSaveButtonClick = handleSubmit(async (data) => {
    try {
      setSaving(true)
      await updateSystemSettings(data)
      navigate('.', { replace: true }) // to revalidate...
      alert({ message: '保存しました' })
    } catch (err) {
      alert({ message: '保存に失敗しました' })
    }
    setSaving(false)
  })

  return (
    <Card variant="outlined">
      <TableContainer>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHead data-testid="table-head-recognition-accuracy">
            <TableRow>
              <TableCell component="th">認識精度</TableCell>
              <TableCell component="th">認識評価値</TableCell>
              <TableCell component="th" />
            </TableRow>
          </TableHead>
          <TableBody data-testid="table-body-recognition-accuracy">
            <TableRow>
              <TableCell component="th">
                <ConfidenceLabelBox>高精度</ConfidenceLabelBox>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Controller
                    name="highMin"
                    control={control}
                    defaultValue={highMin}
                    rules={{
                      validate: (val) => val > getValues('midMin') || ERROR_MSG_HIGH_MIN_SMALL,
                      max: { value: 99, message: ERROR_MSG_HIGH_MIN_RANGE },
                      required: { value: true, message: ERROR_MSG_REQUIRED },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <MyNumericFormat
                        value={value}
                        disabled={saving}
                        onChange={(e) => {
                          const n = parseInt(e.target.value)
                          onChange(isNaN(n) ? null : n)
                        }}
                        endAdornment={<InputAdornment position="end">% 以上</InputAdornment>}
                        testid="high-min"
                      />
                    )}
                  />
                </Box>
              </TableCell>
              <TableCell>
                {errors.highMin && (
                  <Typography variant="body2" color="error">
                    {errors.highMin?.message?.toString()}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th">
                <ConfidenceLabelBox style={{ borderColor: showMidConfidenceColor }}>
                  中精度
                </ConfidenceLabelBox>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Controller
                    name="midMin"
                    control={control}
                    defaultValue={midMin}
                    rules={{
                      min: { value: 0, message: ERROR_MSG_MID_MIN_RANGE },
                      validate: (val) => val < getValues('highMin') || ERROR_MSG_MID_MIN_LARGE,
                      required: { value: true, message: ERROR_MSG_REQUIRED },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <MyNumericFormat
                        value={value}
                        disabled={saving}
                        onChange={(e) => {
                          const n = parseInt(e.target.value)
                          onChange(isNaN(n) ? null : n)
                        }}
                        endAdornment={<InputAdornment position="end">% 以上</InputAdornment>}
                        testid="mid-min"
                      />
                    )}
                  />
                </Box>
              </TableCell>
              <TableCell>
                {errors.midMin && (
                  <Typography variant="body2" color="error">
                    {errors.midMin?.message?.toString()}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th">
                <ConfidenceLabelBox style={{ borderColor: showLowConfidenceColor }}>
                  低精度
                </ConfidenceLabelBox>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <OutlinedInput
                    size="small"
                    disabled
                    margin="dense"
                    value={errors.midMin ? '' : watch('midMin', midMin)}
                    endAdornment={<InputAdornment position="end">% 未満</InputAdornment>}
                  />
                </Box>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <ConfidenceActions>
        <Controller
          name="confidence"
          control={control}
          defaultValue={confidence}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              label="精度区分を表示"
              data-testid="switch-accuracy-settings"
              control={
                <Switch
                  color="primary"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
            />
          )}
        />
        <LoadingButton
          variant="contained"
          color="primary"
          pending={saving}
          disabled={!formState.isValid}
          onClick={handleSaveButtonClick}
          data-testid="save-button"
        >
          保存
        </LoadingButton>
      </ConfidenceActions>
    </Card>
  )
}

export default ConfidenceConfig
